// Deterministic replays of the model_calls log used as a cache, a meter and a health check,
// for Lesson 3.10 ("The Bill and the Clock"). Every token count, latency, hash and sentence
// below was recorded from real runs on 8 October 2026, except the two stand-in hashes named
// as such. The graphics replay those recordings and never call a model.

import { costAtExampleRate, formatCount } from './judge-models';

export { costAtExampleRate, formatCount } from './judge-models';

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const yesNo = (value: boolean) => (value ? 'yes' : 'no');

// --- ops-cache: the log as a cache -----------------------------------------------------------

export const CACHE_CHANGES = ['nothing', 'new-task', 'new-version'] as const;
export type CacheChange = (typeof CACHE_CHANGES)[number];
export const cacheChangeLabels: Record<CacheChange, string> = {
  nothing: 'Nothing',
  'new-task': 'A new task in the project',
  'new-version': 'A new prompt version',
};
export const toCacheChange = (value: string): CacheChange =>
  CACHE_CHANGES.find((change) => change === value) ?? 'nothing';
/**
 * The cache key: a hash of the prompt version, the note and the project's existing tasks.
 * The first was recorded; the other two stand for keys that differ.
 */
export const cacheHashes: Record<CacheChange, string> = {
  nothing: 'd6938bc111ed',
  'new-task': '9f1c0a7e2b44',
  'new-version': '51e7d2c93a08',
};
export const cacheVersions: Record<CacheChange, string> = {
  nothing: 'suggester-v3',
  'new-task': 'suggester-v3',
  'new-version': 'suggester-v4',
};
/** One real ask of note 1 under suggester-v3, as model_calls recorded it. */
export const REAL_ASK = { promptTokens: 318, completionTokens: 30, latencyMs: 1102 } as const;
export const ASK_TOKENS = REAL_ASK.promptTokens + REAL_ASK.completionTokens;
/** The suggestions for note 1, the same on the real ask and on the cached one. */
export const CACHE_SUGGESTIONS: readonly string[] = [
  'Send the agenda by Friday',
  'Check if the projector in the small room is broken',
];
export const CACHE_ASK_CAP = 6;
export interface CacheRow {
  ask: number;
  version: string;
  cached: boolean;
  promptTokens: number;
  completionTokens: number;
  latencyMs: number;
  hash: string;
}
export interface CacheState {
  cacheOn: boolean;
  change: CacheChange;
  rows: CacheRow[];
}
export const initialCache = (): CacheState => ({ cacheOn: true, change: 'nothing', rows: [] });
/** Whether the log already holds a reply under `hash`: the lookup the cache makes. */
export const cacheHolds = (rows: readonly CacheRow[], hash: string) =>
  rows.some((row) => row.hash === hash);
export const canAskCache = (state: CacheState) => state.rows.length < CACHE_ASK_CAP;
/** The row the next ask writes: a hit when the cache is on and the key was seen before. */
export function nextCacheRow(state: CacheState): CacheRow {
  const hash = cacheHashes[state.change];
  const cached = state.cacheOn && cacheHolds(state.rows, hash);
  return {
    ask: state.rows.length + 1,
    version: cacheVersions[state.change],
    cached,
    promptTokens: cached ? 0 : REAL_ASK.promptTokens,
    completionTokens: cached ? 0 : REAL_ASK.completionTokens,
    latencyMs: cached ? 0 : REAL_ASK.latencyMs,
    hash,
  };
}
export type CacheAction =
  | { type: 'ask' }
  | { type: 'toggle-cache' }
  | { type: 'change'; value: CacheChange }
  | { type: 'reset' };
export function cacheTransition(state: CacheState, action: CacheAction): CacheState {
  switch (action.type) {
    case 'reset':
      return initialCache();
    case 'toggle-cache':
      return { ...state, cacheOn: !state.cacheOn };
    case 'change':
      return { ...state, change: action.value };
    case 'ask':
      return canAskCache(state) ? { ...state, rows: [...state.rows, nextCacheRow(state)] } : state;
  }
}
export interface CacheTotals {
  tokens: number;
  /** At the example rate, six decimals. */
  cost: string;
  hits: number;
  asks: number;
  hitRate: string;
}
export function cacheTotals(rows: readonly CacheRow[]): CacheTotals {
  const sum = (pick: (row: CacheRow) => number) =>
    rows.reduce((total, row) => total + pick(row), 0);
  const promptTokens = sum((row) => row.promptTokens);
  const completionTokens = sum((row) => row.completionTokens);
  const hits = rows.filter((row) => row.cached).length;
  return {
    tokens: promptTokens + completionTokens,
    cost: costAtExampleRate(promptTokens, completionTokens),
    hits,
    asks: rows.length,
    hitRate: `${hits} of ${rows.length}`,
  };
}
export const CACHE_START_MESSAGE = 'Press Ask note 1, then press it again.';
export const CACHE_FIRST_MESSAGE = `One real ask: ${ASK_TOKENS} tokens and ${formatCount(REAL_ASK.latencyMs)} ms. Press Ask note 1 again.`;
export const CACHE_HIT_MESSAGE = 'The stored reply was reused: 0 tokens, 0 ms, and still a row.';
export const CACHE_TWICE_MESSAGE = `The same question cost ${ASK_TOKENS} tokens twice.`;
export const CACHE_KEY_MESSAGE =
  'The key changed, so the cache missed on purpose: the right answer may have changed.';
export function cacheSummary(state: CacheState): Summary {
  const last = state.rows.at(-1);
  if (!last) return { tone: 'neutral', message: CACHE_START_MESSAGE };
  if (last.cached) return { tone: 'success', message: CACHE_HIT_MESSAGE };
  const previous = state.rows.at(-2);
  if (!previous) return { tone: 'neutral', message: CACHE_FIRST_MESSAGE };
  if (previous.hash !== last.hash) return { tone: 'neutral', message: CACHE_KEY_MESSAGE };
  return { tone: 'warning', message: CACHE_TWICE_MESSAGE };
}

// --- ops-budget: the log as a meter ----------------------------------------------------------

export const BUDGETS = [200, 50_000] as const;
export type Budget = (typeof BUDGETS)[number];
export const toBudget = (value: string): Budget =>
  BUDGETS.find((budget) => String(budget) === value) ?? 200;
export const budgetLabel = (budget: Budget) => `${formatCount(budget)} tokens`;
export const PEOPLE = ['Maya', 'Ravi'] as const;
export type Person = (typeof PEOPLE)[number];
export const toPerson = (value: string): Person =>
  PEOPLE.find((person) => person === value) ?? 'Maya';
export const BUDGET_REQUESTS = ['ask', 'digest', 'cached'] as const;
export type BudgetRequest = (typeof BUDGET_REQUESTS)[number];
export const budgetRequestLabels: Record<BudgetRequest, string> = {
  ask: 'Ask',
  digest: 'Digest',
  cached: 'Ask the same note again',
};
/** Recorded token counts: an ask of note 1 is 318 + 30, a digest 115 + 62, a cache hit 0. */
export const requestTokens: Record<BudgetRequest, number> = {
  ask: ASK_TOKENS,
  digest: 115 + 62,
  cached: 0,
};
export const WORKSPACE_BUDGET = 3_000_000;
export const WORKSPACE_SENTENCE =
  "Today's model budget for this workspace is used up. It resets at midnight UTC.";
/** The 429 body the service answers, with the numbers as its f-string prints them. */
export const budgetSentence = (used: number, budget: number) =>
  `Today's model budget for your account is used up (${used} of ${budget} tokens). It resets at midnight UTC. You can still add tasks by hand.`;
export const CACHE_HIT_ANSWER = 'answered from the cache';
const requestAnswers: Record<Exclude<BudgetRequest, 'cached'>, string> = {
  ask: 'the suggestions for note 1',
  digest: 'the digest',
};
export type BudgetStatus = 200 | 201 | 429;
export interface BudgetAnswer {
  person: Person;
  request: BudgetRequest;
  status: BudgetStatus;
  tokens: number;
  answer: string;
}
export interface BudgetState {
  budget: Budget;
  person: Person;
  /** Tokens each person's calls spent since midnight UTC. */
  used: Record<Person, number>;
  log: BudgetAnswer[];
}
export const initialBudget = (): BudgetState => ({
  budget: 200,
  person: 'Maya',
  used: { Maya: 0, Ravi: 0 },
  log: [],
});
export type BudgetAction =
  | { type: 'budget'; value: Budget }
  | { type: 'person'; value: Person }
  | { type: 'request'; value: BudgetRequest }
  | { type: 'reset' };
/** One request by the selected person: the cache first, then the meter, then the call. */
function answerRequest(state: BudgetState, request: BudgetRequest): BudgetState {
  const { person, budget } = state;
  const used = state.used[person];
  const entry = (status: BudgetStatus, tokens: number, answer: string): BudgetAnswer => ({
    person,
    request,
    status,
    tokens,
    answer,
  });
  if (request === 'cached')
    return { ...state, log: [...state.log, entry(200, 0, CACHE_HIT_ANSWER)] };
  if (used >= budget)
    return { ...state, log: [...state.log, entry(429, 0, budgetSentence(used, budget))] };
  const tokens = requestTokens[request];
  return {
    ...state,
    used: { ...state.used, [person]: used + tokens },
    log: [...state.log, entry(request === 'ask' ? 201 : 200, tokens, requestAnswers[request])],
  };
}
export function budgetTransition(state: BudgetState, action: BudgetAction): BudgetState {
  switch (action.type) {
    case 'reset':
      return initialBudget();
    case 'budget':
      return { ...state, budget: action.value };
    case 'person':
      return { ...state, person: action.value };
    case 'request':
      return answerRequest(state, action.value);
  }
}
export const budgetStateLabel = (used: number, budget: number) =>
  used >= budget ? 'stopped until midnight UTC' : `${formatCount(budget - used)} left`;
export const usedLine = (person: Person, used: number, budget: number) =>
  `${person} has used ${formatCount(used)} of ${formatCount(budget)} tokens today.`;
export const BUDGET_START_MESSAGE = 'Nothing spent today: press Ask, then press Ask again.';
export const BUDGET_CACHED_MESSAGE = 'Answered from the cache: no tokens, no budget check.';
export function budgetSummary(state: BudgetState): Summary {
  const last = state.log.at(-1);
  if (!last) return { tone: 'neutral', message: BUDGET_START_MESSAGE };
  if (last.request === 'cached') return { tone: 'success', message: BUDGET_CACHED_MESSAGE };
  if (last.status === 429) return { tone: 'warning', message: last.answer };
  return {
    tone: 'neutral',
    message: usedLine(last.person, state.used[last.person], state.budget),
  };
}

// --- ops-backoff: the retry policy ------------------------------------------------------------

export const PROVIDER_SCRIPTS = [
  'ok',
  'rate-busy-ok',
  'rate-four',
  'timeout-ok',
  'unauthorized',
] as const;
export type ProviderScript = (typeof PROVIDER_SCRIPTS)[number];
export const providerScriptLabels: Record<ProviderScript, string> = {
  ok: '200 at once',
  'rate-busy-ok': '429, then 503, then 200',
  'rate-four': '429 four times',
  'timeout-ok': 'A timeout, then 200',
  unauthorized: '401 at once',
};
export const toProviderScript = (value: string): ProviderScript =>
  PROVIDER_SCRIPTS.find((script) => script === value) ?? 'ok';
export type ProviderAnswer = 200 | 401 | 429 | 503 | 'timeout';
/** What the provider answers to each attempt under each option. */
export const providerAnswers: Record<ProviderScript, readonly ProviderAnswer[]> = {
  ok: [200],
  'rate-busy-ok': [429, 503, 200],
  'rate-four': [429, 429, 429, 429],
  'timeout-ok': ['timeout', 200],
  unauthorized: [401],
};
export const MAX_ATTEMPTS = 4;
/** The sleeps between attempts: 2 ** (attempt - 1) seconds. */
export const WAITS: readonly [number, number, number] = [1, 2, 4];
export const RETRIED: readonly ProviderAnswer[] = ['timeout', 429, 503];
export const isRetried = (answer: ProviderAnswer) => RETRIED.includes(answer);
export const REPLY_RESULT = '200: the reply';
export const CREDENTIAL_MESSAGE =
  'Model error 401: This workspace credential is not valid. Reopen the IDE from Mio to refresh it.';
/** The ModelError the fourth failure raises. */
export const gaveUpMessage = (answer: ProviderAnswer) =>
  `The model did not answer after ${MAX_ATTEMPTS} attempts (${answer === 'timeout' ? 'timeout' : `status ${answer}`}).`;
const callerMessage = (answer: ProviderAnswer) =>
  answer === 401 ? CREDENTIAL_MESSAGE : `Model error ${answer}`;
export function policyResult(answer: ProviderAnswer): string {
  if (answer === 200) return REPLY_RESULT;
  if (isRetried(answer)) return `ModelError: ${gaveUpMessage(answer)}`;
  return `${answer} returned to the caller: ${callerMessage(answer)}`;
}
export interface PolicyAttempt {
  attempt: number;
  answer: ProviderAnswer;
  /** Seconds slept before the next attempt, or null when this attempt ended the policy. */
  waitAfter: number | null;
}
export type PolicyOutcome = 'reply' | 'gave-up' | 'returned';
export interface PolicyRun {
  attempts: PolicyAttempt[];
  secondsWaited: number;
  outcome: PolicyOutcome;
  result: string;
}
const answerAt = (answers: readonly ProviderAnswer[], attempt: number): ProviderAnswer =>
  answers[attempt - 1] ?? 200;
/** The lesson's policy: retry a timeout, 429 or 503 after 1, 2 and 4 seconds; four attempts. */
export function runPolicy(answers: readonly ProviderAnswer[]): PolicyRun {
  const attempts: PolicyAttempt[] = [];
  let secondsWaited = 0;
  let attempt = 1;
  let answer = answerAt(answers, attempt);
  while (isRetried(answer) && attempt < MAX_ATTEMPTS) {
    const waitAfter = 2 ** (attempt - 1);
    attempts.push({ attempt, answer, waitAfter });
    secondsWaited += waitAfter;
    attempt += 1;
    answer = answerAt(answers, attempt);
  }
  attempts.push({ attempt, answer, waitAfter: null });
  const outcome: PolicyOutcome =
    answer === 200 ? 'reply' : isRetried(answer) ? 'gave-up' : 'returned';
  return { attempts, secondsWaited, outcome, result: policyResult(answer) };
}
export const waitLabel = (waitAfter: number | null) =>
  waitAfter === null ? 'none' : `${waitAfter} s`;
export interface BackoffState {
  script: ProviderScript;
  run: PolicyRun | null;
}
export const initialBackoff = (): BackoffState => ({ script: 'ok', run: null });
export type BackoffAction =
  { type: 'script'; value: ProviderScript } | { type: 'run' } | { type: 'reset' };
export function backoffTransition(state: BackoffState, action: BackoffAction): BackoffState {
  switch (action.type) {
    case 'reset':
      return initialBackoff();
    case 'script':
      return { script: action.value, run: null };
    case 'run':
      return { ...state, run: runPolicy(providerAnswers[state.script]) };
  }
}
export const BACKOFF_START_MESSAGE = 'Pick an option, then press Run the policy.';
export const BACKOFF_CAP_MESSAGE =
  'Seven seconds of waiting, then a clear sentence instead of a frozen page.';
export const BACKOFF_NO_RETRY_MESSAGE = 'Not retried: a bad token does not get better by waiting.';
export const BACKOFF_COVERED_MESSAGE = 'The retries worked and the caller got a reply.';
export function backoffSummary(state: BackoffState): Summary {
  if (!state.run) return { tone: 'neutral', message: BACKOFF_START_MESSAGE };
  switch (state.run.outcome) {
    case 'gave-up':
      return { tone: 'warning', message: BACKOFF_CAP_MESSAGE };
    case 'returned':
      return { tone: 'warning', message: BACKOFF_NO_RETRY_MESSAGE };
    case 'reply':
      return { tone: 'success', message: BACKOFF_COVERED_MESSAGE };
  }
}

// --- ops-fallback: the log as a health check --------------------------------------------------

export const WINDOW_MINUTES = 5;
export const PAUSE_AT = 3;
export const LATER_MINUTES = [0, 6] as const;
export type LaterMinutes = (typeof LATER_MINUTES)[number];
export const toLaterMinutes = (value: string): LaterMinutes =>
  LATER_MINUTES.find((minutes) => String(minutes) === value) ?? 0;
export interface ModelCall {
  /** The minute the call was made, on the lab's clock. */
  minute: number;
  ok: boolean;
}
export interface FallbackState {
  later: LaterMinutes;
  calls: ModelCall[];
  draft: string;
  /** Tasks added by hand through the form, which never stops working. */
  handTasks: string[];
}
export const initialFallback = (): FallbackState => ({
  later: 0,
  calls: [],
  draft: '',
  handTasks: [],
});
export const DEFAULT_HAND_TASK = 'Send the agenda by Friday';
export type FallbackAction =
  | { type: 'ask'; ok: boolean }
  | { type: 'later'; value: LaterMinutes }
  | { type: 'draft'; value: string }
  | { type: 'add-task' }
  | { type: 'reset' };
export function fallbackTransition(state: FallbackState, action: FallbackAction): FallbackState {
  switch (action.type) {
    case 'reset':
      return initialFallback();
    case 'ask':
      return { ...state, calls: [...state.calls, { minute: state.later, ok: action.ok }] };
    case 'later':
      return { ...state, later: action.value };
    case 'draft':
      return { ...state, draft: action.value };
    case 'add-task':
      return {
        ...state,
        draft: '',
        handTasks: [...state.handTasks, state.draft.trim() || DEFAULT_HAND_TASK],
      };
  }
}
/** Whether a call is a failure the status counts at minute `now`. */
export const countedAt = (call: ModelCall, now: number) =>
  !call.ok && call.minute > now - WINDOW_MINUTES && call.minute <= now;
export const recentFailures = (state: FallbackState) =>
  state.calls.filter((call) => countedAt(call, state.later)).length;
export interface ModelStatus {
  available: boolean;
  recent_failures: number;
  window_minutes: number;
}
/** GET /api/model/status: available until three failures fall inside the window. */
export function modelStatus(state: FallbackState): ModelStatus {
  const failures = recentFailures(state);
  return {
    available: failures < PAUSE_AT,
    recent_failures: failures,
    window_minutes: WINDOW_MINUTES,
  };
}
/** The body as Python's json.dumps prints it. */
export const statusJson = (status: ModelStatus) =>
  `{"available": ${String(status.available)}, "recent_failures": ${status.recent_failures}, "window_minutes": ${status.window_minutes}}`;
export const pausedSentence = (failures: number) =>
  `Suggestions are paused: ${failures} failed calls in the last ${WINDOW_MINUTES} minutes. Add tasks by hand below; asking resumes when the model answers again.`;
export const FAILED_SENTENCE = 'The model could not answer. Try again in a moment.';
export const WAITING_SENTENCE = '2 suggestion(s) waiting for a decision.';
export const NO_SUGGESTIONS_SENTENCE = 'No suggestions waiting for a decision.';
export const ADD_TASK_NOTE = 'always works';
export interface PanelView {
  paused: boolean;
  askDisabled: boolean;
  statusLine: string;
  addTaskEnabled: true;
}
/** What the suggestions panel shows: the page decides to pause from the status alone. */
export function fallbackPanel(state: FallbackState): PanelView {
  const status = modelStatus(state);
  const paused = !status.available;
  const last = state.calls.at(-1);
  const statusLine = paused
    ? pausedSentence(status.recent_failures)
    : last === undefined
      ? NO_SUGGESTIONS_SENTENCE
      : last.ok
        ? WAITING_SENTENCE
        : FAILED_SENTENCE;
  return { paused, askDisabled: paused, statusLine, addTaskEnabled: true };
}
export const FALLBACK_PAUSED_MESSAGE =
  'Three failures in five minutes, so the panel stops asking and points to the form.';
export const FALLBACK_RESUMED_MESSAGE = 'The window emptied, so asking resumed on its own.';
export const fallbackAllowedMessage = (failures: number) =>
  `${failures} failure(s) in the window, so asking is still allowed.`;
export function fallbackSummary(state: FallbackState): Summary {
  const failures = recentFailures(state);
  if (failures >= PAUSE_AT) return { tone: 'warning', message: FALLBACK_PAUSED_MESSAGE };
  const earlierFailures = state.calls.some((call) => !call.ok && call.minute < state.later);
  if (failures === 0 && earlierFailures)
    return { tone: 'success', message: FALLBACK_RESUMED_MESSAGE };
  return { tone: 'neutral', message: fallbackAllowedMessage(failures) };
}
